import type { RefObject } from "react";
import { useCallback, useEffect, useImperativeHandle } from "react";

export type CloseRequestOptions = {
  skipConfirmation?: boolean;
};

export type CloseHandle = {
  isDirty: () => boolean;
  requestDescendantsClose?: () => Promise<boolean>;
  requestClose: (options?: CloseRequestOptions) => Promise<boolean>;
};

export type ComponentController = {
  id: string;
  draftKey: string;
  closeRef: RefObject<CloseHandle | null>;

  child: (key: string | number) => ComponentController;
  getChildren: () => ComponentController[];
  isDirty: () => boolean;
  requestClose: (options?: CloseRequestOptions) => Promise<boolean>;
};

export type ComponentControllerProps = {
  controller?: ComponentController;
};

type ComponentDirtyCheckRegistration = {
  controller?: ComponentController;
  isDirty: boolean | (() => boolean);
  requestDescendantsClose?: () => Promise<boolean>;
  requestClose: (options?: CloseRequestOptions) => boolean | Promise<boolean>;
};

const componentDirtyChecks = new Set<() => boolean>();

export function hasDirtyComponents() {
  return Array.from(componentDirtyChecks).some((isDirty) => isDirty());
}

export function useComponentDirtyCheck({
  controller,
  isDirty,
  requestDescendantsClose,
  requestClose,
}: ComponentDirtyCheckRegistration) {
  const getIsDirty = useCallback(
    () => (typeof isDirty === "function" ? isDirty() : isDirty),
    [isDirty],
  );

  useEffect(() => {
    componentDirtyChecks.add(getIsDirty);
    return () => {
      componentDirtyChecks.delete(getIsDirty);
    };
  }, [getIsDirty]);

  useImperativeHandle(
    controller?.closeRef ?? null,
    () => {
      return {
        isDirty: getIsDirty,
        requestDescendantsClose,
        requestClose: async (options) => {
          if (!getIsDirty()) {
            return true;
          }

          return requestClose(options);
        },
      };
    },
    [getIsDirty, requestClose, requestDescendantsClose],
  );
}

export function createComponentController(id: string): ComponentController {
  const children = new Map<string, ComponentController>();

  const controller: ComponentController = {
    id,
    draftKey: `componentDraft-${id}`,
    closeRef: { current: null },

    child: (key) => {
      const childKey = String(key);

      const existing = children.get(childKey);

      if (existing) {
        return existing;
      }

      const childRuntime = createComponentController(`${id}:${childKey}`);

      children.set(childKey, childRuntime);

      return childRuntime;
    },

    getChildren: () => Array.from(children.values()),

    isDirty: () => {
      const isCurrentComponentDirty =
        controller.closeRef.current?.isDirty() ?? false;

      return (
        isCurrentComponentDirty ||
        Array.from(children.values()).some((childRuntime) =>
          childRuntime.isDirty(),
        )
      );
    },

    requestClose: async (options) => {
      if (controller.closeRef.current) {
        return controller.closeRef.current.requestClose(options);
      }

      for (const childRuntime of children.values()) {
        const canClose = await childRuntime.requestClose(options);

        if (!canClose) {
          return false;
        }
      }

      return true;
    },
  };

  return controller;
}
